import React, {useState} from 'react';
import RibbonNav from "../RibbonNav";
import CustomDropdown from "../reusableComponents/CustomDropdown";
import PlantFinderPlantResults from "./PlantFinderPlantResults";
import {Search} from 'lucide-react';
import '../../css/plantFinderStylings/PlantFinderPageStyling.css';

const PLANTTYPES = [
    {value: 'flower', label: 'Flower'},
    {value: 'fruit', label: 'Fruit'},
    {value: 'herb', label: 'Herb'},
    {value: 'house plant', label: 'House Plant'},
    {value: 'leafy green', label: 'Leafy Green'},
    {value: 'root', label: 'Root'},
    {value: 'vegetable', label: 'Vegetable'}
]

const GROWINGSEASON = [
    {value: 'spring', label: 'Spring'},
    {value: 'summer', label: 'Summer'},
    {value: 'fall', label: 'Fall'},
    {value: 'winter', label: 'Winter'}
]

const LIFECYCLE = [
    {value: 'annual', label: 'Annual'},
    {value: 'biennial', label: 'Biennial'},
    {value: 'perennial', label: 'Perennial'}
]

const SOILPREFERENCE = [
    {value: 'loamy', label: 'Loamy'},
    {value: 'sandy', label: 'Sandy'},
    {value: 'clay', label: 'Clay'},
    {value: 'silty', label: 'Silty'},
    {value: 'peaty', label: 'Peaty'},
    {value: 'chalky', label: 'Chalky'},
    {value: 'saline', label: 'Saline'}
]

const SUNLEVELS = [
    {value: 'full sun', label: 'Full Sun'},
    {value: 'partial sun', label: 'Partial Sun'},
    {value: 'partial shade', label: 'Partial Shade'},
    {value: 'full shade', label: 'Full Shade'}
]


const PLANTSIZE = [
    {value: 'small', label: 'Small (1ft x 1ft)'},
    {value: 'medium', label: 'Medium (2ft x 2ft)'},
    {value: 'large', label: 'Large (3ft x 3ft)'},
]

const STORAGELIFE = [
    {value: 'low', label: 'Short'},
    {value: 'moderate', label: 'Medium'},
    {value: 'high', label: 'Long'}
]

const WATERNEEDS = [
    { value: 'xeric', label: 'Low (drought tolerant)' },
    { value: 'mesic', label: 'Moderate' },
    { value: 'hydric', label: 'High (needs frequent watering)' }
];

const HARDINESSZONES = [
    { value: '1a', label: '1a' },
    { value: '1b', label: '1b' },
    { value: '2a', label: '2a' },
    { value: '2b', label: '2b' },
    { value: '3a', label: '3a' },
    { value: '3b', label: '3b' },
    { value: '4a', label: '4a' },
    { value: '4b', label: '4b' },
    { value: '5a', label: '5a' },
    { value: '5b', label: '5b' },
    { value: '6a', label: '6a' },
    { value: '6b', label: '6b' },
    { value: '7a', label: '7a' },
    { value: '7b', label: '7b' },
    { value: '8a', label: '8a' },
    { value: '8b', label: '8b' },
    { value: '9a', label: '9a' },
    { value: '9b', label: '9b' },
    { value: '10a', label: '10a' },
    { value: '10b', label: '10b' },
    { value: '11a', label: '11a' },
    { value: '11b', label: '11b' },
    { value: '12a', label: '12a' },
    { value: '12b', label: '12b' },
    { value: '13a', label: '13a' },
    { value: '13b', label: '13b' }
];


const PlantFinderPage = () => {

    const [searchTerm, setSearchTerm] = useState('');
    const [plantType, setPlantType] = React.useState("");
    const [growingSeason, setGrowingSeason] = React.useState("");
    const [lifecycle, setLifecycle] = React.useState("");
    const [soilPreference, setSoilPreference] = React.useState("");
    const [sunlightLevels, setSunlightLevels] = React.useState("");
    const [plantSize, setPlantSize] = React.useState("");
    const [storageLife, setStorageLife] = React.useState("");
    const [hardinessZone, setHardinessZone] = React.useState("");
    const [waterNeeds, setWaterNeeds] = React.useState("");

    const [checkboxStates, setCheckboxStates] = useState({
        droughtTolerant: false,
        seedSavingFriendly: false,
        groundCover: false,
        selfPollinating: false,
        climbingViningPlant: false,
        lowMaintenance: false,
        fastGrowth: false,
        nitrogenFixer: false,
        providesWildlifeCover: false,
        continuouslyHarvestable: false,
        allAtOnceHarvest: false,
        goodForCanningOrPreserving: false,
        petSafeNontoxic: false,
        medicinalProperties: false,
        pollinatorFriendly: false,
        organicPestControl: false,
        ornamentalFoliage: false,
        fragrantFlowers: false,
        edibleFlowers: false,
        coldTolerant: false,
        heatTolerant: false,
        containerFriendly: false,
        deerResistant: false,
        companionPlant: false
    });

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setCheckboxStates(prev => ({
            ...prev,
            [name]: checked
        }));
    };


    const handlePlantTypeChange = React.useCallback((plantType) => {
        setPlantType(plantType.value);
    }, []);

    const handleGrowingSeasonChange = React.useCallback((growingSeason) => {
        setGrowingSeason(growingSeason.value);
    }, []);

    const handleLifecycleChange = React.useCallback((lifecycle) => {
        setLifecycle(lifecycle.value);
    }, []);

    const handleSoilPreferenceChange = React.useCallback((soil) => {
        setSoilPreference(soil.value);
    }, []);

    const handleSunlightLevelChange = React.useCallback((sunlight) => {
        setSunlightLevels(sunlight.value);
    }, []);

    const handlePlantSizeChange = React.useCallback((plantSize) => {
        setPlantSize(plantSize.value);
    }, []);

    const handleStorageLifeChange = React.useCallback((storageLife) => {
        setStorageLife(storageLife.value);
    }, []);

    const handleWaterNeedsChange = React.useCallback((waterNeeds) => {
        setWaterNeeds(waterNeeds.value);
    }, []);

    const handleHardinessZoneChange = React.useCallback((hardiness) => {
        setHardinessZone(hardiness.value);
    }, []);


    const filters = {
        searchTerm,
        plantType,
        growingSeason,
        lifecycle,
        soilPreference,
        sunlightLevels,
        plantSize,
        storageLife,
        hardinessZone,
        waterNeeds,
        checkboxStates,
    };

    const handleClearSearchFilters = () => {
        // Reset search term
        setSearchTerm('');

        // Reset all dropdown selections
        setPlantType('');
        setGrowingSeason('');
        setLifecycle('');
        setSoilPreference('');
        setSunlightLevels('');
        setPlantSize('');
        setStorageLife('');
        setHardinessZone('');
        setWaterNeeds('');

        // Reset all checkbox states
        setCheckboxStates({
            droughtTolerant: false,
            seedSavingFriendly: false,
            groundCover: false,
            selfPollinating: false,
            climbingViningPlant: false,
            lowMaintenance: false,
            fastGrowth: false,
            nitrogenFixer: false,
            providesWildlifeCover: false,
            continuouslyHarvestable: false,
            allAtOnceHarvest: false,
            goodForCanningOrPreserving: false,
            petSafeNontoxic: false,
            medicinalProperties: false,
            pollinatorFriendly: false,
            organicPestControl: false,
            ornamentalFoliage: false,
            fragrantFlowers: false,
            edibleFlowers: false,
            coldTolerant: false,
            heatTolerant: false,
            containerFriendly: false,
            deerResistant: false,
            companionPlant: false
        });
    };

    return (
        <div className={`plant-finder-page`}>
            <RibbonNav pageName="Plant Finder"/>
            <div className={`plant-finder-page-body`}>
                <div className={`plant-finder-page-header`}>
                    <div className="plant-finder-page-title">Plant Finder</div>
                </div>

                <div className={`plant-finder-page-search-inputs`}>
                    <div className="plant-finder-search-bar">
                        <Search size={24} strokeWidth={3.5} className={"search-bar-icon"}/>
                        <input
                            type="text"
                            placeholder="Start typing to search for plants..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className={`plant-finder-search-inputs-visual-indicator`}>
                        <div className={`plant-finder-search-inputs-visual-indicator-title`}>Plant search options</div>
                        <div className={`plant-finder-clear-button`}>
                            <button className={`plant-finder-button`} onClick={handleClearSearchFilters}>
                                Clear
                            </button>
                        </div>
                    </div>

                    <div className={`plant-finder-preset-inputs`}>
                        <div className={`plant-finder-dropdown-search-inputs`}>
                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={PLANTTYPES}
                                    value={plantType}
                                    onChange={handlePlantTypeChange}
                                    placeholder="Select a plant type..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={GROWINGSEASON}
                                    value={growingSeason}
                                    onChange={handleGrowingSeasonChange}
                                    placeholder="Select a growing season..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={LIFECYCLE}
                                    value={lifecycle}
                                    onChange={handleLifecycleChange}
                                    placeholder="Select a lifecyle..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={SOILPREFERENCE}
                                    value={soilPreference}
                                    onChange={handleSoilPreferenceChange}
                                    placeholder="Select a soil preference..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={SUNLEVELS}
                                    value={sunlightLevels}
                                    onChange={handleSunlightLevelChange}
                                    placeholder="Select a sunlight level..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={PLANTSIZE}
                                    value={plantSize}
                                    onChange={handlePlantSizeChange}
                                    placeholder="Select a plant size..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={HARDINESSZONES}
                                    value={hardinessZone}
                                    onChange={handleHardinessZoneChange}
                                    placeholder="Select hardiness zone..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={WATERNEEDS}
                                    value={waterNeeds}
                                    onChange={handleWaterNeedsChange}
                                    placeholder="Select water needs..."
                                    className="garden-dropdown-custom"
                                />
                            </div>

                            <div className={`plant-finder-dropdown`}>
                                <CustomDropdown
                                    options={STORAGELIFE}
                                    value={storageLife}
                                    onChange={handleStorageLifeChange}
                                    placeholder="Select a storage life..."
                                    className="garden-dropdown-custom"
                                />
                            </div>
                        </div>

                        <div className={`plant-finder-checkbox-search-inputs`}>
                            <label>
                                <input
                                    className={`plant-finder-checkbox`}
                                    type="checkbox"
                                    name="droughtTolerant"
                                    checked={checkboxStates.droughtTolerant}
                                    onChange={handleCheckboxChange}
                                />
                                Drought Tolerant
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="seedSavingFriendly"
                                    checked={checkboxStates.seedSavingFriendly}
                                    onChange={handleCheckboxChange}
                                />
                                Seed Saving Friendly
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="groundCover"
                                    checked={checkboxStates.groundCover}
                                    onChange={handleCheckboxChange}
                                />
                                Provides ground cover
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="selfPollinating"
                                    checked={checkboxStates.selfPollinating}
                                    onChange={handleCheckboxChange}
                                />
                                Is Self-pollinating
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="climbingViningPlant"
                                    checked={checkboxStates.climbingViningPlant}
                                    onChange={handleCheckboxChange}
                                />
                                Is a climbing/vining plant
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="lowMaintenance"
                                    checked={checkboxStates.lowMaintenance}
                                    onChange={handleCheckboxChange}
                                />
                                Low Maintenance
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="fastGrowth"
                                    checked={checkboxStates.fastGrowth}
                                    onChange={handleCheckboxChange}
                                />
                                Fast Grower
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="nitrogenFixer"
                                    checked={checkboxStates.nitrogenFixer}
                                    onChange={handleCheckboxChange}
                                />
                                Fixes nitrogen into soil
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="providesWildlifeCover"
                                    checked={checkboxStates.providesWildlifeCover}
                                    onChange={handleCheckboxChange}
                                />
                                Provides wildlife with cover
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="continuouslyHarvestable"
                                    checked={checkboxStates.continuouslyHarvestable}
                                    onChange={handleCheckboxChange}
                                />
                                Continuously harvestable
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="allAtOnceHarvest"
                                    checked={checkboxStates.allAtOnceHarvest}
                                    onChange={handleCheckboxChange}
                                />
                                Harvest happens all at once
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="goodForCanningOrPreserving"
                                    checked={checkboxStates.goodForCanningOrPreserving}
                                    onChange={handleCheckboxChange}
                                />
                                Good For Canning or Preserving
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="petSafeNontoxic"
                                    checked={checkboxStates.petSafeNontoxic}
                                    onChange={handleCheckboxChange}
                                />
                                Pet safe and non-toxic
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="medicinalProperties"
                                    checked={checkboxStates.medicinalProperties}
                                    onChange={handleCheckboxChange}
                                />
                                Has Medicinal Properties
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="pollinatorFriendly"
                                    checked={checkboxStates.pollinatorFriendly}
                                    onChange={handleCheckboxChange}
                                />
                                Pollinator Friendly
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="organicPestControl"
                                    checked={checkboxStates.organicPestControl}
                                    onChange={handleCheckboxChange}
                                />
                                Organic Pest Control plant
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="ornamentalFoliage"
                                    checked={checkboxStates.ornamentalFoliage}
                                    onChange={handleCheckboxChange}
                                />
                                Ornamental Foliage
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="fragrantFlowers"
                                    checked={checkboxStates.fragrantFlowers}
                                    onChange={handleCheckboxChange}
                                />
                                Fragrant Flowers
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="edibleFlowers"
                                    checked={checkboxStates.edibleFlowers}
                                    onChange={handleCheckboxChange}
                                />
                                Edible Flowers
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="coldTolerant"
                                    checked={checkboxStates.coldTolerant}
                                    onChange={handleCheckboxChange}
                                />
                                Cold Tolerant
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="heatTolerant"
                                    checked={checkboxStates.heatTolerant}
                                    onChange={handleCheckboxChange}
                                />
                                Heat Tolerant
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="containerFriendly"
                                    checked={checkboxStates.containerFriendly}
                                    onChange={handleCheckboxChange}
                                />
                                Grows Well in Containers
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="deerResistant"
                                    checked={checkboxStates.deerResistant}
                                    onChange={handleCheckboxChange}
                                />
                                Deer Resistant
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    name="companionPlant"
                                    checked={checkboxStates.companionPlant}
                                    onChange={handleCheckboxChange}
                                />
                                Good Companion Plant
                            </label>
                        </div>
                    </div>
                </div>

                <div className={`plant-search-results`}>
                    <PlantFinderPlantResults filters={filters} />
                </div>

            </div>
        </div>
    )
}

export default PlantFinderPage;